import Dinero from 'dinero.js'
import { uniq } from 'lodash'
import store from 'state/store'
import {
  TICKET_STATUSES,
  MEASURE_UNIT,
  ROUNDING_PRECISIONS,
  DEFAULT_AXLE_DATA,
  IBT_ROW_TYPES,
  TICKET_TYPES,
} from 'utils/constants'
import {
  getTotalWeightDeduction,
  getTotalPriceDeduction,
  generateAdjustmentText,
  getNetWeight,
} from 'utils/data-manipulation/common'
import { dateFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import { calcAdjustments, convertUM } from 'utils/helpers'
import { calculateFeePromotionValue } from 'utils/fee-promotion-calculator'

const mockScaleTypes = [
  {
    id: 'Scale1',
    name: 'Scale #1',
  },
  {
    id: 'Scale2',
    name: 'Scale #2',
  },
]

export const statusArray = [
  TICKET_STATUSES.INCOMPLETE,
  TICKET_STATUSES.SCALE_COMPLETE,
  TICKET_STATUSES.PRICE_COMPLETE,
  TICKET_STATUSES.APPROVED,
  TICKET_STATUSES.TRUCK_IN_YARD,
]

export const initialTicketInformation = {
  customer: {
    name: null,
  },
  effectiveDate: null,
  numberOfPassengers: null,
  transportationInfo: {
    isPickup: 'false',
    purchaseOrderNumber: '',
    purchaseOrderId: '',
    billOfLading: '',
    driverName: '',
    carrierName: '',
    carrierLookup: null,
    carrierNumber: '',
    trailerNumber: '',
    containerNumber: '',
    transportationType: '',
  },
  scaleType: mockScaleTypes[0].id,
  status: { value: statusArray[0].id },
  statusHistory: [],
  truckWeight: {
    gross: null,
    tare: null,
    net: 0,
  },
  footerInformation: {
    freightCost: '',
    grossWeight: '',
    tareWeight: '',
    netWeight: '',
    totalValue: '',
  },
  freightCost: {
    cost: 0,
    chargeBack: 0,
    userSet: false,
    id: '',
  },
}

export const getInitialTotalInformation = ({ defaultNumberOfAxles = 1, feesAndPromotionsData }) => [
  {
    id: 'load-total',
    rowType: IBT_ROW_TYPES.LOAD_TOTAL,
    code: 'Load Total',
    gross: 0,
    tare: 0,
    um: null,
    netWeight: 0,
    netValue: 0,
  },
  {
    id: 'truck-total',
    rowType: IBT_ROW_TYPES.TRUCK_TOTAL,
    code: 'Truck Total',
    gross: 0,
    tare: 0,
    um: null,
    netWeight: 0,
    scaleButtonClicked: false,
    isManualGross: false,
    isManualTare: false,
    axleWeights: [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
      ...DEFAULT_AXLE_DATA,
      axleNumber: index + 1,
    })),
  },
  ...(feesAndPromotionsData?.Items || []).map(item => ({
    id: item.id,
    rowType: item.type.toLowerCase(),
    code: item.name,
    gross: null,
    tare: null,
    um: null,
    netWeight: null,
    price: item.amount,
    netValue: item.amountType === 'FLAT' ? item.amount : 0,
    amountType: item.amountType,
    eligiblePaymentTypes: item.eligiblePaymentTypes,
    showPaymentMethodSpecificIcon: item.eligiblePaymentTypes?.length < 4,
  })),
]

export const initialLoadInformation = {
  truckWeight: {
    gross: 0,
    net: 0,
    tare: 0,
  },
  loadWeight: {
    gross: 0,
    tare: 0,
    deductions: 0,
    net: 0,
  },
  netWeight: 0,
  netValue: 0,
  feesAndPromotionsTotal: 0,
}

export const defaultVehicleInformation = {
  id: '',
  vin: '',
  year: '',
  make: '',
  model: '',
  color: '',
  body: '',
  titleNumber: '',
  titleState: '',
  plateNumber: '',
  plateState: '',
  registrationExpiration: null,
}

export const calculateTotalInfoFields = (materialRows, options = {}) => {
  const { showWeightDecimals = false } = options

  const { defaultYardUm } = store.getState()?.settings || {}

  let loadGross = 0
  let loadTare = 0
  let weightDeductions = 0
  let priceDeductions = 0
  let totalNet = 0
  let totalValue = 0

  materialRows.forEach(materialRow => {
    const { rowType, gross = 0, tare = 0, price = 0, netValue = 0, deductions = [], um = defaultYardUm } = materialRow
    const formattedGross = Number(parseFloat(gross).toFixed(showWeightDecimals ? 1 : 0))
    const formattedTare = Number(parseFloat(tare || 0).toFixed(showWeightDecimals ? 1 : 0))

    if (rowType === IBT_ROW_TYPES.SCRAP_VEHICLE) return

    loadGross = roundTo(loadGross + formattedGross, ROUNDING_PRECISIONS.WEIGHT)
    loadTare = roundTo(loadTare + formattedTare, ROUNDING_PRECISIONS.WEIGHT)
    const grossMinusTare = roundTo(formattedGross - formattedTare, ROUNDING_PRECISIONS.WEIGHT)
    weightDeductions = roundTo(
      weightDeductions + getTotalWeightDeduction(deductions, grossMinusTare),
      ROUNDING_PRECISIONS.WEIGHT,
    )
    priceDeductions = roundTo(
      priceDeductions + getTotalPriceDeduction(deductions, convertUM(grossMinusTare * price, defaultYardUm, um)),
      ROUNDING_PRECISIONS.COST,
    )
    totalNet = roundTo(
      totalNet + getNetWeight({ gross: formattedGross, tare: formattedTare, deductions }),
      ROUNDING_PRECISIONS.WEIGHT,
    )
    totalValue = roundTo(totalValue + netValue, ROUNDING_PRECISIONS.COST)
  })
  const totalDeductions = generateAdjustmentText(weightDeductions, priceDeductions)

  return {
    loadGross,
    loadTare,
    totalNet,
    loadNetValue: totalValue,
    weightDeductions,
    totalDeductions,
  }
}

export const incrementCheckNumber = (currentCheckNumber, incrementAmount = 1) => {
  const incrementedNumber = (Number(currentCheckNumber) + incrementAmount).toString()
  const lengthDifference = (currentCheckNumber?.toString()?.length || 0) - incrementedNumber.length
  return lengthDifference > 0 ? `${'0'.repeat(lengthDifference)}${incrementedNumber}` : incrementedNumber
}

export const getCustomerId = (supplierRef, inboundTicketData, customers, data) => {
  if (supplierRef?.current?.id) {
    return supplierRef?.current?.id
  }

  return inboundTicketData
    ? inboundTicketData?.customer?.id
    : customers?.find(cust => data?.customer?.name === cust.customerCommonIdentifierString)?.id
}

export const formatVehicleData = data => {
  const vehicle = data
  delete vehicle.id
  if (vehicle.registrationExpiration) {
    const [date] = new Date(data.registrationExpiration).toISOString().split('T')
    vehicle.registrationExpiration = date
  } else {
    delete vehicle.registrationExpiration
  }
  return vehicle
}

export const formatIbt = data => {
  const materialRows = data.materials
    .filter(material => material.materialTypeId)
    .map(material => ({
      ...material,
      grossImages: material.status?.grossImages,
      tareImages: material.status?.tareImages,
      materialCode: material.materialType?.code,
      materialName: material.materialType?.materialName,
      materialTypeId: material.materialType?.id,
    }))
  return {
    id: data.id,
    materials: materialRows,
    effectiveDate: data.effectiveDate,
    status: data.status,
    customer: data.customer,
    numberOfPassengers: data.customer?.passengers,
    customerId: data.customer?.id || null,
    ticketId: data.ticketId,
    paymentId: data.paymentId,
    transportationInfo: data.transportationInfo,
    scrapVehicles: data.scrapVehicles,
    truckWeight: data.truckWeight,
    loadWeight: data.loadWeight,
    netValue: Number(Dinero({ ...data.netValue }).toUnit()),
    netWeight: data.netWeight,
    notes: data.notes,
    taxRate: data.taxRate,
    vehicleId: data.vehicleId,
    locationId: data.locationId,
    contactId: data.contactId,
    freightCost: {
      cost: data.freightCost?.cost || 0,
      chargeBack: data.freightCost?.chargeBack || 0,
      userSet: data.freightCost?.userSet || false,
      userChargebackSet: data.freightCost?.userChargebackSet || false,
      id: data.freightCost?.id || undefined,
    },
    feeOrPromotion: data.feeOrPromotion,
  }
}

export const isDifferentialOverstepped = (totalApiRef, thresholdAmount, thresholdUm) => {
  if (!totalApiRef) return false
  const truckTotalRow = totalApiRef?.current?.getRow('truck-total')
  const { gross, tare } = truckTotalRow
  const truckTotal = parseInt(gross, 10) - parseInt(tare, 10)
  const loadTotal = totalApiRef?.current?.getRow('load-total')?.netWeight || 0
  const differential = truckTotal - loadTotal
  const threshold =
    thresholdUm === 'pounds' ? parseInt(thresholdAmount, 10) : loadTotal * (parseInt(thresholdAmount, 10) / 100)
  return differential > threshold
}

export const updateChargebackFeesAndPromotionsRows = (
  totalApiRef,
  chargeback = 0,
  feeOrPromotionFormVals,
  loadNetValue = null,
) => {
  const feeOrPromotionRows = feeOrPromotionFormVals ? Object.values(feeOrPromotionFormVals) : []
  totalApiRef.current.updateRows(feeOrPromotionRows.map(row => ({ id: row.id, _action: 'delete' })))
  totalApiRef.current.updateRows([{ id: 'freight-chargeback', _action: 'delete' }])

  if (chargeback > 0) {
    totalApiRef.current.updateRows([
      {
        id: 'freight-chargeback',
        rowType: IBT_ROW_TYPES.FREIGHT_CHARGEBACK,
        code: 'Freight Charge',
        gross: null,
        tare: null,
        totalDeductions: null,
        netWeight: null,
        netValue: chargeback,
      },
    ])
  }

  // Use passed loadNetValue if available, otherwise get from grid
  const netValueForCalculation =
    loadNetValue !== null ? loadNetValue : totalApiRef.current.getRow('load-total')?.netValue
  let updatedFeesAndPromotionsTotal = -chargeback

  const updatedFeesAndPromotionsRows = feeOrPromotionRows.map(row => {
    const calculatedValue = calculateFeePromotionValue(row, netValueForCalculation, true) // Force recalculation
    updatedFeesAndPromotionsTotal = roundTo(updatedFeesAndPromotionsTotal + calculatedValue, ROUNDING_PRECISIONS.COST)

    return {
      id: row.id,
      rowType: row.type.toLowerCase(),
      code: row.name,
      gross: null,
      tare: null,
      um: null,
      netWeight: null,
      price: row.amount,
      netValue: calculatedValue,
      amountType: row.amountType,
      eligiblePaymentTypes: row.eligiblePaymentTypes,
      showPaymentMethodSpecificIcon: row.eligiblePaymentTypes?.length < 4,
    }
  })

  totalApiRef.current.updateRows(updatedFeesAndPromotionsRows)
  return { updatedFeesAndPromotionsTotal }
}

export const updateDifferentialRow = (
  totalApiRef,
  truckTotal,
  loadTotal,
  chargeback = 0,
  feeOrPromotionFormVals,
  loadNetValue = null,
) => {
  const differential = truckTotal - loadTotal
  if (differential > 0) {
    totalApiRef.current.updateRows([
      {
        id: 'differential',
        rowType: IBT_ROW_TYPES.DIFFERENTIAL,
        code: 'Differential',
        gross: null,
        tare: null,
        totalDeductions: null,
        netWeight: differential,
        netValue: null,
      },
    ])
    // this function is used to maintain the row order (will delete and then recreate rows)
    updateChargebackFeesAndPromotionsRows(totalApiRef, chargeback, feeOrPromotionFormVals, loadNetValue)
  } else {
    totalApiRef.current.updateRows([{ id: 'differential', _action: 'delete' }])
  }
}

export const getTruckTotal = (scaleIbtData = {}) => {
  const { gross, tare } = scaleIbtData.truckWeight
  return gross?.amount - tare?.amount || 0
}

export const getTotals = (materials, truckWeight, showWeightDecimals) => {
  const calculateMaterialTotal = material => {
    const gross = Number(parseFloat(material?.weightAndPrice?.gross?.amount).toFixed(showWeightDecimals ? 1 : 0))
    const tare = Number(parseFloat(material?.weightAndPrice?.tare?.amount || 0).toFixed(showWeightDecimals ? 1 : 0))
    return Number(roundTo(gross - tare - calcAdjustments(material).weightAdjustments, ROUNDING_PRECISIONS.WEIGHT))
  }
  const truckTotal = Number(truckWeight?.gross?.amount) - Number(truckWeight?.tare?.amount)

  const loadTotal = materials
    .filter(material => material.materialTypeId !== undefined)
    .reduce(
      (aggregate, material) => roundTo(aggregate + calculateMaterialTotal(material), ROUNDING_PRECISIONS.WEIGHT),
      0,
    )

  return { truckTotal, loadTotal }
}

export const isDifferentialOversteppedScaleView = (differential, loadTotal, thresholdAmount, thresholdUM) => {
  const threshold =
    thresholdUM === 'pounds' ? parseInt(thresholdAmount, 10) : (loadTotal * parseInt(thresholdAmount, 10)) / 100
  return differential > threshold
}

export const generatePaymentMethodText = (paymentsData = []) =>
  uniq(paymentsData.map(payment => payment?.paymentMethod?.toUpperCase())).length > 1
    ? 'Multiple'
    : paymentsData?.[0]?.paymentMethod || ''

export const generateCheckNumberText = (paymentsData = []) =>
  paymentsData
    .map(payment => payment.checkNumber)
    .filter(checkNumber => !!checkNumber) // remove non-check payments
    .sort((a, b) => Number(a) - Number(b)) // sort ascending order
    .join(', ')

export const getDateTime = (dateTime, originalDateTime) => {
  const currentDateTime = new Date()
  if (!dateTime) return currentDateTime
  const original = dateFormatter(new Date(originalDateTime).getTime())
  const newDateTime = dateFormatter(new Date(dateTime).getTime())
  if (original === newDateTime) return new Date(originalDateTime)
  const pickerDateTime = new Date(dateTime)
  return new Date(
    pickerDateTime.getFullYear(),
    pickerDateTime.getMonth(),
    pickerDateTime.getDate(),
    currentDateTime.getHours(),
    currentDateTime.getMinutes(),
    currentDateTime.getSeconds(),
    currentDateTime.getMilliseconds(),
  )
}

/**
 * Check if the inbound ticket data is a transfer
 * @param inboundTicketData
 * @returns {boolean|boolean}
 */
export const isTransferFromInboundTicketData = inboundTicketData => {
  if (!inboundTicketData) return false
  return inboundTicketData?.ticketType === TICKET_TYPES.TRANSFER || Boolean(inboundTicketData?.transferredFromObtId)
}
